import React, { FC, useContext } from "react";
import { genericLoadOptions, addBaseUrl } from "../../../../lib/DurationPicker/utils";
import { useEndpoints } from "../../../../hooks/useEndpoints";
import { IUser2 } from "../../../../models/user copy";
import CustomSelectInput from "../../../CustomSelectInput/CustomSelectInput";
import { USER_TYPES } from "../../../../enums/user";
import style from "./SelectUsers.module.scss";
import { XIcon, CheckIcon } from "@phosphor-icons/react";
import Typhography from "../../../Typhography/Typhography";
import { GroupBase, OptionProps } from "react-select";
import { ThemeContext } from "@emotion/react";
import CustomAvatar from "../../../CustomAvatar/CustomAvatar";
import { formatFullName } from "../../../../utils/functions";

const UserOption: React.ComponentType<
  OptionProps<any, boolean, GroupBase<any>>
> = ({ innerProps, isDisabled, data }) => {
  const selectedValues = useContext(ThemeContext);
  if (isDisabled) return null;

  const user: IUser2 = data.value;
  const { firstName, lastName, email, _id } = user;
  const imagePath = (user as any).image?.image;
  const isSelected = Array.isArray(selectedValues) && selectedValues.some((item: any) => item._id === _id);
  const initials = `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();

  return (
    <div
      className={`container__custom__select__input__option ${!isSelected ? "not__selected" : ""}`}
      {...innerProps}
    >
      {isSelected && <CheckIcon size={16} weight="bold" />}
      <CustomAvatar
        name={initials}
        src={imagePath ? addBaseUrl(1, imagePath) : undefined}
        className={style.optionAvatar}
        textClassName={style.optionAvatarText}
      />
      <div className={style.optionInfo}>
        <Typhography size="bold-14" type="span">
          {formatFullName(firstName, lastName)}
        </Typhography>
        <Typhography size="regular-12" type="span" className={style.optionEmail}>
          {email}
        </Typhography>
      </div>
    </div>
  );
};

export const SelectUsers: FC<{
  label: string;
  onChange: (newValue: any) => void;
  value: any;
}> = ({ value, onChange }) => {
  const data = Array.isArray(value) ? value : [];
  const { getUsers } = useEndpoints();

  const promiseOptions = genericLoadOptions(
    getUsers,
    (user: IUser2) => ({
      label: formatFullName(user.firstName, user.lastName),
      value: user,
    }),
    {
      type: USER_TYPES.ATHLETE,
      populate: "image.image",
    }
  );

  const onClickDeleteUser = (id: string) => {
    onChange(data.filter((item) => item._id !== id));
  };

  return (
    <div className="d-flex flex-column gap-3">
      <ThemeContext.Provider value={data}>
        <CustomSelectInput
          autoHeight
          label={`Cerca atleti`}
          isOptionSelected={(option: any) => {
            return data.some((coach) => coach._id === option.value._id);
          }}
          onChange={(value) => {
            if (!data.some((item) => item._id === value.value._id))
              onChange([...data, value.value]);
          }}
          value={null}
          placeholder="Cerca per nome, cognome o email..."
          loadOptions={promiseOptions}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          components={{
            Option: UserOption,
          }}
        />
      </ThemeContext.Provider>
      <div className="d-flex flex-column gap-1">
        <Typhography size="bold-12">
          {value.length} atleti selezionati
        </Typhography>
        <div className="d-flex flex-wrap gap-1">
          {data.map((athlete) => {
            const imagePath = (athlete as any).image?.image;
            const initials = `${athlete.firstName?.[0] ?? ""}${athlete.lastName?.[0] ?? ""}`.toUpperCase();
            return (
              <div className={style.chip} key={athlete._id}>
                <CustomAvatar
                  name={initials}
                  src={imagePath ? addBaseUrl(1, imagePath) : undefined}
                  className={style.chipAvatar}
                  textClassName={style.chipAvatarText}
                />
                <Typhography size="medium-12" type="span">
                  {formatFullName(athlete.firstName, athlete.lastName)}
                </Typhography>
                <button
                  className={style.chipRemove}
                  onClick={() => onClickDeleteUser(athlete._id)}
                  type="button"
                  aria-label="Rimuovi atleta"
                >
                  <XIcon size={10} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
